import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
import MainLayout from '../layouts/main';
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import RoleBasedGuard from '../guards/RoleBasedGuard';
import PermissionBasedGuard from '../guards/PermissionBasedGuard';
// config
import { PATH_AFTER_LOGIN } from '../config';
import { PATH_AUTH } from './paths';
// components
import LoadingScreen from '../components/LoadingScreen';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/portal')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: 'portal',
      element: (
        <AuthGuard>
          <PermissionBasedGuard hasContent perms={['customer', 'admin', 'readfeedback']}>
            <DashboardLayout />
          </PermissionBasedGuard>
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        { path: 'home', element: <GeneralApp /> },
        { path: 'fretboard', element: <ToolsFretboard /> },
        { path: 'harmonic-minor', element: <ToolsHarmonicMinor /> },
        { path: 'segovia', element: <ToolsSegovia /> },
        {
          path: 'tools',
          children: [
            { element: <Navigate to="/portal/home" replace />, index: true },
            { path: 'kanban', element: <Kanban /> },
          ],
        },
        {
          path: 'admin',
          children: [
            { path: 'links', element: (<PermissionBasedGuard hasContent perms={['admin']}><ToolsLinkShortener /></PermissionBasedGuard>) },
            { path: 'links/new', element: (<PermissionBasedGuard hasContent perms={['admin']}><ToolsLinkCreateEdit /></PermissionBasedGuard>) },
            { path: 'links/:linkId/edit', element: (<PermissionBasedGuard hasContent perms={['admin']}><ToolsLinkCreateEdit /></PermissionBasedGuard>) },
            { path: 'pastes', element: (<PermissionBasedGuard hasContent perms={['admin']}><ToolsPasteBin /></PermissionBasedGuard>) },
            { path: 'pastes/new', element: (<PermissionBasedGuard hasContent perms={['admin']}><ToolsPasteCreate /></PermissionBasedGuard>) },
            { path: 'pastes/:pasteId/edit', element: (<PermissionBasedGuard hasContent perms={['admin']}><ToolsPasteCreate /></PermissionBasedGuard>) },
            {
              path: 'invite-codes',
              element: (
                <PermissionBasedGuard hasContent perms={['admin']}>
                  <AdminInviteCodes />
                </PermissionBasedGuard>
              ),
            },
            {
              path: 'feature-teasers',
              element: (
                <PermissionBasedGuard hasContent perms={['admin']}>
                  <AdminFeatureTeasers />
                </PermissionBasedGuard>
              ),
            },
            {
              path: 'segovia-diagrams',
              element: (
                <PermissionBasedGuard hasContent perms={['admin']}>
                  <AdminSegoviaDiagrams />
                </PermissionBasedGuard>
              ),
            },
          ],
        },
        {
          path: 'feedback',
          children: [
            {
              path: 'inbox',
              element: (
                <PermissionBasedGuard hasContent perms={['admin', 'readfeedback']}>
                  <FeedbackInbox />
                </PermissionBasedGuard>
              ),
            },
          ],
        },
        { path: '*', element: <Navigate to="/" replace /> },
      ],
    },
    {
      path: 'auth',
      element: <LogoOnlyLayout />,
      children: [
        { element: <Navigate to={PATH_AUTH.login} replace />, index: true },
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'new-password', element: <NewPassword /> },
        { path: 'verify', element: <VerifyCode /> },
      ],
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        /* { path: 'coming-soon', element: <ComingSoon /> },
        { path: 'maintenance', element: <Maintenance /> },
        { path: 'pricing', element: <Pricing /> },
        { path: 'payment', element: <Payment /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> }, */
        { path: '*', element: <Navigate to="/" replace /> },
      ],
    },
    {
      path: '/',
      children: [
        { element: <HomePage />, index: true },
        { path: 'paste/:pasteId', element: <PasteView /> },
        // { path: ':linkId', element: <Redirect /> },
        /* { path: 'about-us', element: <About /> },
        { path: 'contact-us', element: <Contact /> },
        { path: 'faqs', element: <Faqs /> }, */
      ],
    },
    {
      path: 'link',
      children: [
        { element: <Navigate to="/" replace />, index: true },
        { path: ':linkId', element: <Redirect /> },
      ],
    },
    { path: '*', element: <Navigate to="/" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const NewPassword = Loadable(lazy(() => import('../pages/auth/NewPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));

// DASHBOARD

// GENERAL
const GeneralApp = Loadable(lazy(() => import('../pages/dashboard/GeneralApp')));
const GeneralEcommerce = Loadable(lazy(() => import('../pages/dashboard/GeneralEcommerce')));
const GeneralAnalytics = Loadable(lazy(() => import('../pages/dashboard/GeneralAnalytics')));
const GeneralBanking = Loadable(lazy(() => import('../pages/dashboard/GeneralBanking')));
const GeneralBooking = Loadable(lazy(() => import('../pages/dashboard/GeneralBooking')));

// TOOLS
const ToolsLinkShortener = Loadable(lazy(() => import('../pages/dashboard/ToolsLinkShortener')));
const ToolsLinkCreateEdit = Loadable(lazy(() => import('../pages/dashboard/ToolsLinkCreateEdit')));
const ToolsPasteBin = Loadable(lazy(() => import('../pages/dashboard/ToolsPasteBin')));
const ToolsPasteCreate = Loadable(lazy(() => import('../pages/dashboard/ToolsPasteCreate')));
const ToolsFretboard = Loadable(lazy(() => import('../pages/dashboard/ToolsFretboard')));
const ToolsHarmonicMinor = Loadable(lazy(() => import('../pages/dashboard/ToolsHarmonicMinor')));
const ToolsSegovia = Loadable(lazy(() => import('../pages/dashboard/ToolsSegovia')));
const AdminInviteCodes = Loadable(lazy(() => import('../pages/dashboard/AdminInviteCodes')));
const AdminFeatureTeasers = Loadable(lazy(() => import('../pages/dashboard/AdminFeatureTeasers')));
const AdminSegoviaDiagrams = Loadable(lazy(() => import('../pages/dashboard/AdminSegoviaDiagrams')));
const FeedbackInbox = Loadable(lazy(() => import('../pages/dashboard/FeedbackInbox')));
const PasteView = Loadable(lazy(() => import('../pages/PasteView')));

// ECOMMERCE
const EcommerceShop = Loadable(lazy(() => import('../pages/dashboard/EcommerceShop')));
const EcommerceProductDetails = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductDetails')));
const EcommerceProductList = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductList')));
const EcommerceProductCreate = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductCreate')));
const EcommerceCheckout = Loadable(lazy(() => import('../pages/dashboard/EcommerceCheckout')));

// INVOICE
const InvoiceList = Loadable(lazy(() => import('../pages/dashboard/InvoiceList')));
const InvoiceDetails = Loadable(lazy(() => import('../pages/dashboard/InvoiceDetails')));
const InvoiceCreate = Loadable(lazy(() => import('../pages/dashboard/InvoiceCreate')));
const InvoiceEdit = Loadable(lazy(() => import('../pages/dashboard/InvoiceEdit')));

// BLOG
const BlogPosts = Loadable(lazy(() => import('../pages/dashboard/BlogPosts')));
const BlogPost = Loadable(lazy(() => import('../pages/dashboard/BlogPost')));
const BlogNewPost = Loadable(lazy(() => import('../pages/dashboard/BlogNewPost')));

// USER
const UserProfile = Loadable(lazy(() => import('../pages/dashboard/UserProfile')));
const UserCards = Loadable(lazy(() => import('../pages/dashboard/UserCards')));
const UserList = Loadable(lazy(() => import('../pages/dashboard/UserList')));
const UserAccount = Loadable(lazy(() => import('../pages/dashboard/UserAccount')));
const UserCreate = Loadable(lazy(() => import('../pages/dashboard/UserCreate')));

// APP
const Chat = Loadable(lazy(() => import('../pages/dashboard/Chat')));
const Mail = Loadable(lazy(() => import('../pages/dashboard/Mail')));
const Calendar = Loadable(lazy(() => import('../pages/dashboard/Calendar')));
const Kanban = Loadable(lazy(() => import('../pages/dashboard/Kanban')));

// TEST RENDER PAGE BY ROLE
const PermissionDenied = Loadable(lazy(() => import('../pages/dashboard/PermissionDenied')));

// MAIN
const HomePage = Loadable(lazy(() => import('../pages/Home')));
const About = Loadable(lazy(() => import('../pages/About')));
const Contact = Loadable(lazy(() => import('../pages/Contact')));
const Faqs = Loadable(lazy(() => import('../pages/Faqs')));
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
const Pricing = Loadable(lazy(() => import('../pages/Pricing')));
const Payment = Loadable(lazy(() => import('../pages/Payment')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const Page403 = Loadable(lazy(() => import('../pages/Page403')));
const Page404 = Loadable(lazy(() => import('../pages/Page404')));
const Redirect = Loadable(lazy(() => import('../pages/Redirect')));
